import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Translate } from '../../utils/translate';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { catchError, filter, Observable, of, switchMap } from 'rxjs';
import { User } from '../../model/user.model';
import { UserService } from 'src/app/core/services/user.service';
import { LoanOptions } from '../../model/loanRequest.enum';
import { FeedbackModalComponent } from '../feedback-modal/feedback-modal.component';
import { OrderService } from 'src/app/core/services/order.service';
import { MatDialog } from '@angular/material/dialog';
import i18next from 'i18next';

@Component({
  selector: 'app-driver-data',
  templateUrl: './driver-data.component.html',
  styleUrls: ['./driver-data.component.scss']
})
export class DriverDataComponent implements OnInit {
  @Input() driverData!: any;
  @Input() loanData!: any;
  @Input() extraData: any;
  @Input() isModify?: boolean;

  @Output() getDetailEmitter = new EventEmitter();

  searchControl = new FormControl('', [Validators.minLength(3)]);
  filteredOptions: Observable<User[]> = of([]);
  fullNameControl = false;
  nisControl = false;
  isSelected = false;
  /* loan options */
  fullName = '';

  /* errors */
  nisError = false;
  nisErrorLength = false;
  nameError = false;
  noNameError = false;
  nisErrorContract = false;
  nameErrorContract = false;

  errorNisName = false;
  errorSearch = false;
  nisComplete = false;
  errorSearchContract = false;
  nisOK = false;

  /* Selector de horas */
  hoursMinutes: string[] = [];
  selectedHour = '';
  actualHour!: string;
  today = new Date();

  form: FormGroup = this.fb.group({});
  driverForm: FormGroup = this.fb.group({});
  loanOptions = LoanOptions;

  constructor(
    public translate: Translate,
    private fb: FormBuilder,
    private userService: UserService,
    private orderService: OrderService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initDriverForm();
  }

  initDriverForm(): void {
    this.getAutocomplete();
    if (this.driverData?.type === 'INTERNAL') {
      this.driverForm = this.fb.group({
        nis: [this.driverData?.userId, [Validators.required]],
        fullName: [this.driverData?.fullName, [Validators.required]]
      });
      this.searchControl.setValue(this.driverData?.fullName);
      this.nisAndFullNameControl();
    } else {
      this.driverForm = this.fb.group({
        fullName: [
          this.driverData?.externalData.fullName,
          [Validators.required]
        ]
      });
    }
  }

  getAutocomplete() {
    this.filteredOptions = this.searchControl.valueChanges.pipe(
      filter(() => !this.nisOK),
      switchMap((value) =>
        value!.length >= 3
          ? this.userService.getUsersName(value!).pipe(
              catchError(() => {
                this.isSelected = false;
                this.errorSearch = true;
                this.nameError = true;
                return of([]);
              }),
              switchMap((users) => {
                this.errorSearch = false;
                this.isSelected = false;

                if (users.length === 0 && !this.isSelected) {
                  //Si no hay usuarios que coincidan con la busqueda
                  this.nameError = true;
                  this.noNameError = false;
                  return of(users);
                } else if (
                  users.length > 0 &&
                  !this.isSelected &&
                  this.fullName !== this.searchControl.value
                ) {
                  this.noNameError = true;
                  this.nameError = false;
                  return of(users);
                } else {
                  this.nameError = false;
                  this.noNameError = false;
                  return of(users);
                }
              })
            )
          : of([])
      )
    );
  }

  nisAndFullNameControl(): void {
    const nis = this.driverForm.get('nis');
    const name = this.driverForm.get('fullName');

    this.driverForm.get('nis')?.valueChanges.subscribe((value) => {
      if (value && value.length > 0 && !this.isSelected && !this.nisComplete) {
        this.setValidateNis();
        name?.disable();
        this.driverForm.get('fullName')?.patchValue('');
        this.errorNisName = false;
      } else if (!value) {
        this.nisError = false;
        this.nisErrorLength = false;
        this.errorNisName = true;
        name?.enable();
        this.nisOK = false;
        this.isSelected = false;
      }
    });

    this.searchControl.valueChanges.subscribe((value) => {
      if (value && value.length > 0 && !this.isSelected) {
        nis?.disable({ emitEvent: false });
        this.driverForm.get('nis')?.patchValue('');
        this.nisComplete = false;
        this.errorNisName = false;
      } else if (!value) {
        this.nameError = false;
        this.noNameError = false;
        this.errorNisName = true;
        this.nisComplete = false;
        nis?.enable();
        this.driverForm.get('nis')?.patchValue('');
      }
    });
  }

  onOptionSelected(event: any): void {
    this.nameError = false;
    this.isSelected = true;
    this.noNameError = false;
    const selectedUserName = event.option.value.fullName;
    const selectedUserNis = event.option.value.id;
    this.driverForm.get('fullName')?.patchValue(selectedUserName);
    this.driverForm
      .get('nis')
      ?.patchValue(selectedUserNis, { emitEvent: true });
    this.driverForm.get('nis')?.disable({ emitEvent: false });
    this.driverForm.get('fullName')?.enable();
    this.searchControl.setValue(selectedUserName);
    this.nisAndFullNameControl();
    this.setValidateNis();
    this.fullName = selectedUserName;
    this.nisComplete = true;
  }

  setValidateNis() {
    const nis = this.driverForm.get('nis')?.value;
    const name = this.driverForm.get('fullName');

    this.nisError = false;
    this.nisErrorLength = false;

    if (nis && nis.length < 6 && nis.length > 0) {
      name?.disable({ emitEvent: false });
      this.nisErrorLength = true;
      this.nisError = false;
      this.errorNisName = false;
    } else if (nis?.length === 0) {
      name?.enable({ emitEvent: false });
      this.errorNisName = true;
    } else if (nis && nis.length >= 6) {
      this.userService.getUserNisName(nis).subscribe({
        next: (resp: string) => {
          if (resp) {
            this.isSelected = true;
            this.nisOK = true;
            this.driverForm.get('fullName')?.patchValue(resp);
            this.searchControl.setValue(resp);
            this.nisError = false;
            this.nisErrorLength = false;
            this.errorNisName = false;
          } else {
            this.nisError = true;
            this.errorNisName = false;
          }
        },
        error: () => {
          this.nisError = true;
          this.nisErrorLength = false;
          this.errorNisName = false;
        }
      });
    }
  }

  updateInfoNis() {
    this.setValidateNis();
  }

  sendLoanModify() {
    let loanDataResult;
    if (this.loanData) {
      loanDataResult = {
        deliveryDate: this.loanData.deliveryDate,
        returnDate: this.loanData.returnDate,
        reason: this.loanData.reason,
        reasonText: this.loanData.reasonObservations,
        loanQuota: this.loanData.loanQuota ? this.loanData.loanQuota : 0,
        comments: this.extraData.observations
          ? this.extraData.observations
          : '',
        carpoolCenter: this.loanData.carpoolCenter,
        driverDataDTO: {
          driverType: this.driverData.type,
          externalDriverData:
            this.driverData.type === 'EXTERNAL'
              ? {
                  identificationDocument:
                    this.loanData.driver.externalData.identificationDocument,
                  fullName: this.driverForm.get('fullName')?.value,
                  phoneNumber: this.loanData.driver.externalData.phoneNumber,
                  email: this.loanData.driver.externalData.email
                }
              : null,
          internalDriverData:
            this.loanData.driver.type === 'INTERNAL'
              ? {
                  nis: this.driverForm.get('nis')?.value
                }
              : null,
          driverLicense: this.driverData.driverLicense ? true : false,
          address: this.driverData.address ? this.driverData.address : null,
          zipCode: this.driverData.zipCode ? this.driverData.zipCode : null,
          city: this.driverData.city ? this.driverData.city : null
        },
        contactInfo: {
          phoneNumber: this.extraData.contactInfo.phoneNumber,
          email: this.extraData.contactInfo.email
        },
        controllerUserId: this.extraData.controller
          ? this.extraData.controller.id
          : null,
        delegatedApproverUserId: this.extraData.delegatedApprover
          ? this.extraData.delegatedApprover.id
          : null
      };
      this.orderService
        .modifyLoanOrder(loanDataResult, this.extraData.requestNumber)
        .subscribe({
          next: () => {
            this.dialog.open(FeedbackModalComponent, {
              width: '400px',
              data: {
                text: i18next.t('loanModified'),
                close: true,
                theme: 'ligth'
              }
            });
            this.getDetailEmitter.emit();
          },
          error: () => {
            this.dialog.open(FeedbackModalComponent, {
              width: '400px',
              data: {
                text: i18next.t('loanErrorModify'),
                close: true,
                theme: 'ligth'
              }
            });
          },
          complete: () => {
            console.log('Fetch complete');
          }
        });
    }
  }
}
